// The app half of the Work transport: the desktop bridge (the Tauri RPA bridge,
// the computer-use permission gate, the desktop-target registry), the turn POST
// bound to the chat sidebar and billing caches, the viewed-document reads, the
// Evidence and Slide Builder stream consumers this chat owns, the turn-end cache
// reactions and the Work emits. The run lifecycle and the submit pipeline are
// `useProjectTransportCore` in `@heidi/agents`.
import {
  handleSlideDeleteChunk,
  handleSlideUpdateChunk,
} from '@/components/slide-builder/hooks/useSlideUpdateListener';
import { TabType } from '@/container/ScribeTabs/index.v2.types';
import { invalidateShellCachesOnEvent } from '@/features/agent-events/invalidate-agent-event-shell-caches';
import { lettersQueryOptions } from '@/features/integrations-letters/letter-sender-v2/hooks/query/useQueryLetters';
import { useDesktopPermissionGate } from '@/features/rpa/desktop-permission-gate';
import { isValidDesktopInstanceId } from '@/features/rpa/desktop-target/desktopInstanceId';
import { readNativeInstanceId } from '@/features/rpa/desktop-target/nativeInstanceId';
import { resolveDesktopTargetTurn } from '@/features/rpa/desktop-target/resolveDesktopTargetTurn';
import {
  resolveWorkBillingAnalyticsProps,
  trackProjectChatErrorOnce,
  trackWorkEvent,
  WorkLaunchEventName,
} from '@/models/analytics/features/work';
import { rpaBridge } from '@/tauri/rpa';
import {
  useDesktopTargetViewSelection,
  useProjectTransportCore,
  type ProjectTransport,
  type ProjectTransportOptions,
  type ProjectTransportPorts,
  type RunStreamEventObserver,
} from '@heidi/agents';
import type {
  ProjectTurnDesktopPort,
  TrackWorkApprovalActioned,
  TrackWorkBilledEvent,
  WorkspaceSnapshotV1,
} from '@heidi/agents/contracts';
import {
  extractSlideStreamUpdates,
  type SlideStreamUpdate,
  type ViewedDocumentReads,
} from '@heidi/agents/data';
import { invalidateCreditAllowance } from '@heidi/commerce/data';
import { useQueryDocumentById, useQuerySessionById } from '@heidi/session/data';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import canonicalize from 'canonicalize';
import { useMemo } from 'react';
import { getProjectClientCapabilities } from '../clientCapabilities';
import { invalidateLists, useSubmitProjectTurn } from '../hooks';
import { handleEvidenceChunkStreamEvent } from '../state/evidence-sessions';
import { useWorkspaceSnapshot } from '../workspace/useWorkspaceSnapshot';

/** The session's consult note, read only while the note is on screen. */
const useViewedNoteContent = (sessionId: string) =>
  useQuerySessionById({
    sessionId,
    select: (session) => session?.consult_note?.result ?? null,
  }).data;

/** A session document's body and name, read only while it is on screen. */
const useViewedSessionDocument = (sessionId: string, documentId: string) =>
  useQueryDocumentById({
    sessionId,
    documentId,
    select: (doc) =>
      doc ? { content: doc.content ?? null, title: doc.name ?? null } : null,
  }).data;

/** One letter from the session's letter list. */
const useViewedSessionLetter = (sessionId: string, letterId: string, enabled: boolean) =>
  useQuery({
    ...lettersQueryOptions(sessionId),
    enabled,
    select: (letters) => letters.find((entry) => entry.id === letterId),
  }).data;

/** The reads behind the viewed-document context: two session queries and the letter list. */
const VIEWED_DOCUMENT_READS: ViewedDocumentReads = {
  tabs: {
    note: TabType.NOTE,
    document: TabType.DOCUMENT,
    prechart: TabType.PRECHART,
    letter: TabType.LETTER,
  },
  useNoteContent: useViewedNoteContent,
  useSessionDocument: useViewedSessionDocument,
  useSessionLetter: useViewedSessionLetter,
};

/**
 * Applies one event's slide patches in order, awaiting each. `add_slides`
 * returns several `slide_update`s in a single block; when the deck is not yet
 * in the atom the first one fetches it and the rest merge into that result,
 * instead of every slide racing its own fetch and clobbering the atom.
 */
const applySlideStreamUpdates = async (updates: SlideStreamUpdate[]): Promise<void> => {
  for (const update of updates) {
    if (update.type === 'slide_delete') handleSlideDeleteChunk(update);
    else await handleSlideUpdateChunk(update);
  }
};

/**
 * The run's stream consumers outside the Work domain, which this chat is the one
 * owner of: Evidence sub-agent chunks fold into the shared Evidence timeline, and
 * `edit_slide` / `add_slides_to_presentation` / `delete_slide` results patch the
 * open deck. The background runner and the overlay pass none.
 */
const observeForeignStreamEvent: RunStreamEventObserver = (_id, event) => {
  handleEvidenceChunkStreamEvent(event);
  const slideUpdates = extractSlideStreamUpdates(event);
  if (slideUpdates.length > 0) void applySlideStreamUpdates(slideUpdates);
};

/**
 * The desktop bridge for a turn: the Tauri RPA bridge, the app shell's
 * computer-use permission gate (ruling 15), the desktop-target registry and this
 * installation's native id. Memoised on the gate and the query client, so the
 * core's submit callback follows them as it did when it read both directly.
 */
const useProjectTurnDesktopPort = (): ProjectTurnDesktopPort => {
  const queryClient = useQueryClient();
  const { ensurePermissions } = useDesktopPermissionGate();
  return useMemo<ProjectTurnDesktopPort>(
    () => ({
      isAvailable: () => rpaBridge.isAvailable(),
      ensureComputerUsePermissions: () => ensurePermissions('computer_use'),
      resolveDesktopTarget: ({ enabled, override }) =>
        resolveDesktopTargetTurn(queryClient, {
          desktopTargetEnabled: enabled,
          override,
        }),
      getClientCapabilities: getProjectClientCapabilities,
      // Read on every call, never cached: the core asks on each desktop target-mode turn.
      getInstanceId: async () => {
        const instanceId = await readNativeInstanceId();
        return instanceId !== null && isValidDesktopInstanceId(instanceId)
          ? instanceId
          : null;
      },
      signProjectTurn: async ({ projectId, turnId, targetId, unsignedBody }) => {
        // The backend canonicalizes the same unsigned JSON with RFC 8785;
        // the native key signs its SHA-256 digest, along with routing identity.
        const canonicalBody = canonicalize(unsignedBody);
        if (canonicalBody === undefined) {
          throw new Error('Projects turn body could not be canonicalized');
        }
        return rpaBridge.desktopTargetSignProjectTurn({
          projectId,
          turnId,
          targetId,
          canonicalBody,
        });
      },
    }),
    [ensurePermissions, queryClient]
  );
};

/** `work_run_completed`, with the Work billing columns where the transport placed them. */
const trackWorkRunCompleted: TrackWorkBilledEvent = (withBilling) =>
  trackWorkEvent(
    WorkLaunchEventName.WorkRunCompleted,
    withBilling(resolveWorkBillingAnalyticsProps())
  );

/** `work_message_submitted`, with the Work billing columns where the transport placed them. */
const trackWorkMessageSubmitted: TrackWorkBilledEvent = (withBilling) =>
  trackWorkEvent(
    WorkLaunchEventName.WorkMessageSubmitted,
    withBilling(resolveWorkBillingAnalyticsProps())
  );

const trackWorkApprovalActioned: TrackWorkApprovalActioned = (props) =>
  trackWorkEvent(WorkLaunchEventName.WorkApprovalActioned, props);

/** One module-level object, so the core's stream observer and submit callback stay stable. */
const projectTransportPorts: ProjectTransportPorts = {
  useSubmitTurn: useSubmitProjectTurn,
  invalidateLists,
  useDesktopPort: useProjectTurnDesktopPort,
  viewedDocumentReads: VIEWED_DOCUMENT_READS,
  onForeignStreamEvent: observeForeignStreamEvent,
  turnSettled: {
    invalidateCreditAllowance: () => {
      void invalidateCreditAllowance();
    },
    invalidateShellCaches: invalidateShellCachesOnEvent,
  },
  trackWorkRunCompleted,
  trackWorkMessageSubmitted,
  trackWorkApprovalActioned,
  trackProjectChatErrorOnce,
};

export type UseProjectTransportOptions = {
  /**
   * Override the canvas-derived workspace snapshot (Work-home
   * ``openArtifactAtom``). Used by Work-in-session where the LHS tabs are
   * the screen and no canvas is mounted.
   */
  workspace?: WorkspaceSnapshotV1;
  /** Limit desktop-target resolution to explicit computer-use commands. */
  desktopTargetRequirement?: ProjectTransportOptions['desktopTargetRequirement'];
};

/**
 * The Work transport, bound to the app. The canvas snapshot is read on every
 * render and an embedder's own snapshot wins over it; the view's manual computer
 * choice comes off the desktop-target view selection the host provides.
 */
export const useProjectTransport = (
  projectId: string | undefined,
  options?: UseProjectTransportOptions
): ProjectTransport => {
  const desktopTargetOverride = useDesktopTargetViewSelection()?.override ?? null;
  const canvasWorkspace = useWorkspaceSnapshot();
  return useProjectTransportCore(
    projectId,
    {
      workspace: options?.workspace ?? canvasWorkspace,
      desktopTargetRequirement: options?.desktopTargetRequirement,
      desktopTargetOverride,
    },
    projectTransportPorts
  );
};
